import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { openSupportChatWithMessage } from "@/core/pylon/open-support-chat";
import { cn } from "@/core/utils/utils";

/**
 * What an owner can do when a request ends badly.
 *
 * Three endings leave nothing running and nothing saved: the assistant runs
 * out of steps, the request stops, or it answers with nothing. Each said so
 * and stopped there, so the only move left was to type the same words again
 * and hope, and after the second time there was nothing at all.
 *
 * So: send it again, which sends the same words rather than asking the owner
 * to remember them, and tell Volca, which opens support with the request and
 * what happened to it already written. Support otherwise opens empty, and
 * "the AI did not work" is not something anybody can act on.
 *
 * It sits below the message rather than inside it. The words are what the
 * owner said; this is what became of them, and they are not the same thing.
 */
export const TurnFailure = ({
  note,
  request,
  detail,
  onResend,
  disabled,
  className,
}: {
  /** What happened, in the owner's terms. Left out when the reply said it. */
  note?: string;
  /** The words they asked for, sent again as they are. */
  request: string;
  /** The same thing said to support, with the part they should not have to explain. */
  detail: string;
  onResend: (request: string) => void;
  disabled: boolean;
  /** Lets the caller hang it under a right-aligned bubble. */
  className?: string;
}) => (
  // A dot in a reserved gutter, the way a row that needs attention is marked
  // everywhere else here, rather than a banner or a badge. It is the only red
  // in the conversation, so it does not have to be loud to be found.
  <Stack
    direction="row"
    gap="none"
    align="start"
    className={cn("flex items-start gap-2 pt-1 text-sm", className)}
  >
    <span
      aria-hidden
      className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-fill-negative"
    />
    <Stack gap="none" align="start" className="flex min-w-0 flex-col gap-1">
      {!!note && <p className="text-secondary-text">{note}</p>}
      <Stack direction="row" gap="xs" align="center" className="flex gap-3">
        {!!request && (
          <Button
            size="xs"
            variant="text"
            className="h-auto p-0"
            disabled={disabled}
            onClick={() => onResend(request)}
          >
            Send it again
          </Button>
        )}
        <Button
          size="xs"
          variant="text"
          className="h-auto p-0"
          onClick={() =>
            openSupportChatWithMessage(
              `${detail}\n\nWhat I asked for:\n${request}`,
            )
          }
        >
          Tell Volca
        </Button>
      </Stack>
    </Stack>
  </Stack>
);
